import axios from "axios";
import { getLoginToken, StandardAxiosConfig } from "../common";
import { v4 as uuidv4 } from "uuid";
import { ProagricaTypes } from "../../models";
import { API } from "..";
import { GeoData } from "@rogoag/shared-ts";

const RESPONSE_TYPE = "code";

const SCOPES = ["sync", "openid", "profile", "referencedata", "offline_access"] as const;

export class ProagricaAPI {
	private baseUrl: string;

	constructor() {
		this.baseUrl = `${import.meta.env.VITE_ROGO_API_URL}/proagrica`;
	}

	private async _setProagricaState(nonce: string) {
		await axios.put(`${this.baseUrl}/state?nonce=${nonce}`, undefined, StandardAxiosConfig());
	}

	public async login(url: string) {
		const nonce = _generateNonce();
		const authUrl = _getAuthUrl(url, nonce);
		await this._setProagricaState(nonce);

		return authUrl;
	}

	public async refreshToken() {
		const remainingSeconds = _getDecodedAccessToken().payload.exp - Math.floor(Date.now() / 1000);
		if (remainingSeconds > 180) {
			return;
		}

		await axios.post(`${this.baseUrl}/refresh-token`, null, {
			headers: {
				Authorization: `Bearer ${getLoginToken()}`,
				"proagrica-refresh-token": _getRefreshToken(),
			},
		});

		// call get user to force the token to be stored in local storage
		await API.Authentication.getCurrentUser();
	}

	public getSyncIds() {
		const jwtSyncIds = JSON.parse(_getDecodedAccessToken().payload.syncids) as { id: number; p: string }[];
		const ids = jwtSyncIds.map((syncId) => syncId.id);
		return ids;
	}

	public samplingRecordsToGeoJson(
		records: ProagricaTypes.SoilSamplingRecord[] | ProagricaTypes.SoilSamplingPWORecord[],
	) {
		// @ts-ignore
		return {
			type: "FeatureCollection",
			features: records.map(async (record) => {
				const WKB = "WKB" in record ? Buffer.from(record.WKB) : record.WKT;
				const geoJSONGeometry = await GeoData.toGeoJSON(WKB, "WKB");
				return {
					geometry: geoJSONGeometry,
					type: "Feature",
					properties: {
						//measure: record.Measure.Value
					},
				};
			}),
		} as GeoJSON.FeatureCollection;
	}

	public async getGrower(grower_uuid: string | null = null, sync_id: number): Promise<ProagricaTypes.Grower> {
		const url = `${this.baseUrl}/grower/${grower_uuid}?sync_id=${sync_id}`;
		const response = await axios.get(url, axiosConfig());
		return (response.data || []) as ProagricaTypes.Grower;
	}

	private async _getGrowers(sync_id: number): Promise<ProagricaTypes.Grower[]> {
		const url = `${this.baseUrl}/growers/${sync_id}`;
		const response = await axios.get(url, axiosConfig());
		return (response.data || []) as ProagricaTypes.Grower[];
	}

	public async getGrowers(): Promise<ProagricaTypes.Grower[]> {
		const syncIds = this.getSyncIds();
		const growers = (await Promise.all(syncIds.map((syncId) => this._getGrowers(syncId)))).flat(1);
		return growers;
	}

	public async getFarm(farm_uuid: string, sync_id: number): Promise<ProagricaTypes.Farm> {
		const url = `${this.baseUrl}/farm/${farm_uuid}?sync_id=${sync_id}`;
		const response = await axios.get(url, axiosConfig());
		return response.data as ProagricaTypes.Farm;
	}

	public async getFarmsForGrower(grower_uuid: string, sync_id: number): Promise<ProagricaTypes.GrowerFarm[]> {
		const url = `${this.baseUrl}/grower/${grower_uuid}/farms?sync_id=${sync_id}`;
		const response = await axios.get(url, axiosConfig());
		return (response.data || []) as ProagricaTypes.GrowerFarm[];
	}

	public async getFarmsForGrowers(
		grower_ids: string[],
		sync_id: number,
	): Promise<Array<{ farms: ProagricaTypes.GrowerFarm[]; grower: string }>> {
		const url = `${this.baseUrl}/growers/farms?sync_id=${sync_id}`;
		const response = await axios.post(url, grower_ids, axiosConfig());
		return response.data as Array<{ farms: ProagricaTypes.GrowerFarm[]; grower: string }>;
	}

	public async getFieldsForGrowerFarms(
		grower: ProagricaTypes.Grower,
		farms: ProagricaTypes.GrowerFarm[],
		sync_id: number,
	): Promise<ProagricaTypes.Field[]> {
		const url = `${this.baseUrl}/grower/farms/fields?sync_id=${sync_id}`;
		const requestData = {
			grower: { ID: grower.ID, Name: grower.Name, SyncID: grower.SyncID },
			farms: farms.map((farm) => ({ ID: farm.ID, IsDeleted: farm.IsDeleted })),
		};
		const response = await axios.post(url, requestData, axiosConfig());
		return response.data as ProagricaTypes.Field[];
	}

	public async getManagementZones(field_uuid: string, sync_id: number): Promise<ProagricaTypes.ManagementZone[]> {
		const url = `${this.baseUrl}/field/${field_uuid}/management-zones?sync_id=${sync_id}`;
		const response = await axios.get(url, axiosConfig());
		return (response.data || []) as ProagricaTypes.ManagementZone[];
	}

	public async getManagementZone(zone_id: string, sync_id: number): Promise<ProagricaTypes.ManagementZone> {
		const url = `${this.baseUrl}/management-zone/${zone_id}?sync_id=${sync_id}`;
		const response = await axios.get(url, axiosConfig());
		return response.data as ProagricaTypes.ManagementZone;
	}

	public async getFieldsForGrowerAndFarm(
		grower_uuid: string,
		farm_uuid: string,
		sync_id: number,
	): Promise<ProagricaTypes.GrowerFarmField[]> {
		const url = `${this.baseUrl}/grower/${grower_uuid}/farm/${farm_uuid}/fields?sync_id=${sync_id}`;
		const response = await axios.get(url, axiosConfig());
		return (response.data || []) as ProagricaTypes.GrowerFarmField[];
	}

	public async getFields(access_token: string | null = null, sync_id: number): Promise<ProagricaTypes.Field[]> {
		const url = `${this.baseUrl}/fields?sync_id=${sync_id}&start_time=2024-01-01T15:49:28Z`;
		const response = await axios.get(url, axiosConfig());
		return (response.data || []) as ProagricaTypes.Field[];
	}

	public async getField(field_uuid: string, sync_id: number): Promise<ProagricaTypes.Field> {
		const url = `${this.baseUrl}/field/${field_uuid}?sync_id=${sync_id}`;
		const response = await axios.get(url, axiosConfig());
		return response.data as ProagricaTypes.Field;
	}

	public async getSamplingItems(field_uuid: string, sync_id: number): Promise<ProagricaTypes.SamplingActivity[]> {
		const samplingTypes = [
			// 'SoilSamplingPlan',
			"SoilSamplingRecommendation", // equivalent of INT ?
			// 'SoilSamplingWorkOrder',
			"SoilSamplingApplication", // equivalent of EXE ?
		];
		const samplingItems = await Promise.all(
			samplingTypes.map(async (samplingType) => {
				const url = `${this.baseUrl}/field/${field_uuid}/sampling/${samplingType}?sync_id=${sync_id}`;
				const response = await axios.get(url, axiosConfig());
				const samplingActivities = (response.data || []) as ProagricaTypes.SoilSamplingListItem[];
				const undeletedSamplingItems = samplingActivities.filter((activity) => !activity.IsDeleted);
				return await Promise.all(
					undeletedSamplingItems.map(async (activity) => {
						const activityResponse = await axios.get(
							`${this.baseUrl}/sampling/${samplingType}/${activity["ID"]}?sync_id=${sync_id}`,
							axiosConfig(),
						);
						return activityResponse.data as ProagricaTypes.SamplingActivity;
					}),
				);
			}),
		);
		return samplingItems.flat(2) as ProagricaTypes.SamplingActivity[];
	}
}

interface JwtHeader {
	alg: string;
	kid: string;
	typ: string;
	x5t: string;
}

interface JwtPayload {
	nbf: number;
	exp: number;
	iss: string;
	aud: string[];
	nonce: string;
	iat: number;
	at_hash: string;
	s_hash: string;
	sid: string;
	sub: string;
	auth_time: number;
	idp: string;
	family_name: string;
	given_name: string;
	amr: string[];
	syncids: string;
	scope: string[];
}

interface DecodedJWT {
	header: JwtHeader;
	payload: JwtPayload;
}

function headers() {
	return {
		Authorization: `Bearer ${getLoginToken()}`,
		"Proagrica-Token": _getAccessToken(),
		"Proagrica-Refresh-Token": _getRefreshToken(),
	};
}

function axiosConfig() {
	return {
		headers: headers(),
	};
}

function _getRefreshToken() {
	const proagrica_refresh_token = localStorage.getItem("proagrica_refresh_token");
	if (!proagrica_refresh_token) {
		throw new Error("No Proagrica refresh token provided");
	}
	return proagrica_refresh_token;
}

function _getAccessToken() {
	const proagrica_access_token = localStorage.getItem("proagrica_access_token");
	if (!proagrica_access_token) {
		throw new Error("No Proagrica token provided");
	}
	return proagrica_access_token;
}

function _getDecodedAccessToken(): DecodedJWT {
	const token = _getAccessToken();

	const [header, payload] = token.split(".");

	// Decode base64 URL-safe encoded header and payload
	const decodedHeader = atob(header).toString();
	const decodedPayload = atob(payload).toString();

	// Convert to JSON
	const headerJson = JSON.parse(decodedHeader) as JwtHeader;
	const payloadJson = JSON.parse(decodedPayload) as JwtPayload;

	return {
		header: headerJson,
		payload: payloadJson,
	};
}

function _getAuthUrl(url: string, nonce: string) {
	const url_encoded = encodeURIComponent(url);
	const state = `${url_encoded},${nonce}`;

	let authUrl: string = `${import.meta.env.VITE_AGX_AUTH_BASE_URL}`;
	authUrl += `/${import.meta.env.VITE_AGX_AUTH_ENDPOINT}`;
	authUrl += `?response_type=${RESPONSE_TYPE}`;
	authUrl += `&client_id=${import.meta.env.VITE_AGX_CLIENT_ID}`;
	authUrl += `&redirect_uri=${import.meta.env.VITE_AGX_ROGO_REDIRECT_URI}`;
	authUrl += `&scope=${SCOPES.join("%20")}`;
	authUrl += `&state=${state}`;

	return authUrl;
}

function _generateNonce() {
	return `${uuidv4()}.${Date.now().valueOf().toString()}`;
}
